import { Divider, Switch, Typography, Box, Chip, Button, UserFlag, Checkbox } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useAuth } from 'src/utils';

export default function Filters({ 
    selectedTypes, 
    onFilterChange, 
    allTypes, 
    toggleSelectedTypes,
    handleShowLocations, 
    showFavouritesOnly, 
    handleCurrentViewChange, 
    onToggleFavourites, 
    showTravellers, 
    setShowTravellers, 
    showRoutes,
    setShowRoutes,
    toggleIsExpanded,
    selectedCountries,
    setSelectedCountries,
    allCountries, 
    showFriendsOnly, 
    setShowFriendsOnly }) {

    const { user } = useAuth();
    const isMobile = useIsMobile();
    const font = isMobile ? "body2" : "body3";

    const showLocations = selectedTypes.length > 0 || selectedCountries.length > 0;

    const isCategoryDisabled = (categoryName) => {
        if (categoryName === "Hidden Gems") {
            return user?.role !== "Admin";
        }
        return false;
    };

    const handleTopBarClick = (newView) => {
        handleCurrentViewChange(newView);
        if (isMobile) {
            toggleIsExpanded();
        }
    };

    const handleCountryClick = (countryName) => {
        if (selectedCountries.includes(countryName)) {
            setSelectedCountries(selectedCountries.filter(c => c !== countryName));
        } else {
            setSelectedCountries([...selectedCountries, countryName]);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%', 
            overflow: 'hidden', 
            ml: isMobile ? 0 : 2,
            pt: isMobile ? 'var(--safe-area-top)' : 0,
            pb: isMobile ? 'var(--safe-area-bottom)' : 0,
            transition: 'all 0.4s ease-in-out'}}>

            <Typography variant="h4">
                Filters:
            </Typography>
            
            <Box className="scrollBox" sx={{paddingRight: 2, flex: 1, pb: 3}}>

            <Box sx={{
                border: '2px solid #D3D3D3', 
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '10px',
                padding: 1}}>

                <Typography variant="h6">
                    Show Travellers
                </Typography>
                {!user && <Typography variant={font}>Members Only</Typography>}
                <Switch disabled={!user} checked={showTravellers} size="small" onChange={(e) => setShowTravellers(e.target.checked)} />
            </Box>

            <Box sx={{
                border: '2px solid #D3D3D3', 
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '15px',
                padding: 1}}>

                <Typography variant="h6">
                    Show Locations
                </Typography>
                <Switch 
                    checked={showLocations} 
                    size="small" 
                    onChange={handleShowLocations} />

            </Box>

            <Box sx={{
                border: '2px solid #D3D3D3', 
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '15px',
                padding: 1}}>

                <Typography variant="h6">
                    Show Featured Routes
                </Typography>
                <Switch 
                    checked={showRoutes} 
                    size="small" 
                    onChange={(e) => setShowRoutes(e.target.checked)} />

            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: '10px',
                }}
                >
                <Typography variant="h6">Categories</Typography>
                <Checkbox
                    size="small"
                    checked={selectedTypes.length > 0}
                    onChange={() => toggleSelectedTypes()}
                />
            </Box>


            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: '10px', }}>
                {allTypes.map(type => {
                    const isSelected = selectedTypes.includes(type);
                    return (
                    <Chip
                        key={type}
                        disabled={isCategoryDisabled(type)}
                        size="small"
                        clickable
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        onClick={() => onFilterChange(type)}
                        label={
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                {type === "Hidden Gems" ? (
                                <>
                                    <img src={"/icons/hidden-gem.png"} alt="Hidden Gem" width={16} height={16} />
                                    <Typography variant={font} fontWeight="bold">
                                        Hidden Gems
                                    </Typography>
                                </>
                                ) : (
                                    <Typography variant={font} fontWeight="bold">
                                        {type}
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                    );
                })}
            </Box>

            <Divider sx={{mt: 2}}/>
        
            <Box sx={{
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '15px'}}>

                <Typography variant="h6">
                    Show Favourites
                </Typography>
                {!user && <Typography variant={font}>Members Only</Typography>}
                <Switch 
                    checked={showFavouritesOnly} 
                    size="small" 
                    onChange={onToggleFavourites}
                    disabled={selectedTypes.length === 0 || !user} />

            </Box>

            <Box sx={{
                borderRadius: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '15px'}}>

                <Typography variant="h6">
                    Show Following
                </Typography>
                <Switch 
                    checked={showFriendsOnly} 
                    size="small" 
                    onChange={(e) => setShowFriendsOnly(e.target.checked)}
                    disabled={!showTravellers} />

            </Box> 

            <Divider />

            {/* Country Filter */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: '10px',
                }}
                >
                <Typography variant="h6">Countries</Typography>
                <Checkbox
                    size="small"
                    checked={selectedCountries.length !== 0}
                    onChange={() => {
                        if (selectedCountries.length === 0) {
                            setSelectedCountries(allCountries.map(c => c.name));
                        } else {
                            setSelectedCountries([]); 
                        }
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: '10px' }}>
            {allCountries.map((country) => {
                const isSelected = selectedCountries.includes(country.name);
                return (
                <Chip
                    key={country.name}
                    size="small"
                    clickable
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => handleCountryClick(country.name)}
                    label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <UserFlag countryCode={country.code} />
                        <Typography variant={font} fontWeight="bold">
                            {country.short}
                        </Typography>
                    </Box>
                    }
                />
                );
            })}
            </Box>
            </Box>

             <Box mt={1} display="flex" justifyContent="space-between" alignItems="center" px={3}>
                
                <Typography
                    variant={"body2"}
                    onClick={() => handleTopBarClick("AttractionsTravellersList")}
                    fontWeight="bold"
                    sx={{ cursor: 'pointer' }}>
                    Back
                </Typography>

                <Button onClick={() => handleTopBarClick("AttractionsTravellersList")} variant="contained" size="small">
                    Apply
                </Button>
            </Box>

        </Box>
    );
}
